// Phase B.4 — Role-wise Permission Matrix editor.
// Renders the ~95-key matrix grouped by module with search, expand/collapse,
// select-all per module, and reset-to-default per role.
// Wires GET /api/permissions/keys, GET /api/permissions/role/{role}, PUT /api/permissions/role/{role}.

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import api from "../../lib/api";
import { API } from "../../App";
import { toast } from "sonner";
import {
  CaretDown,
  CaretRight,
  MagnifyingGlass,
  Check,
  ArrowCounterClockwise,
  FloppyDisk,
  ShieldCheck,
} from "@phosphor-icons/react";

const LEGACY_ROLES = [
  { key: "admin",          label: "Admin",                   color: "#002FA7" },
  { key: "md",             label: "MD",                      color: "#7C3AED" },
  { key: "cfo",            label: "CFO",                     color: "#0EA5E9" },
  { key: "marketing_head", label: "Digital Marketing Head",  color: "#F59E0B" },
  { key: "manager",        label: "Manager",                 color: "#10B981" },
  { key: "hr",             label: "HR",                      color: "#EC4899" },
  { key: "bdm",            label: "BDM",                     color: "#6366F1" },
  { key: "bde",            label: "BDE",                     color: "#64748B" },
  { key: "dealer",         label: "Dealer",                  color: "#94A3B8" },
  { key: "customer",       label: "Customer",                color: "#94A3B8" },
];

const PermissionMatrixTab = ({ user }) => {
  const [activeRole, setActiveRole] = useState("bde");
  const [keysByModule, setKeysByModule] = useState({});  // { module: [{key,label}, ...] }
  const [allKeys, setAllKeys] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isCustomized, setIsCustomized] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [dirty, setDirty] = useState(false);

  // Load full key catalog once
  useEffect(() => {
    axios
      .get(`${API}/permissions/keys`)
      .then((r) => {
        setKeysByModule(r.data.by_module || {});
        setAllKeys((r.data.keys || []).map((k) => k.key));
      })
      .catch(() => toast.error("Failed to load permission catalog"));
  }, []);

  // Load permissions for the active role
  const loadRole = useCallback(async (roleKey) => {
    setLoading(true);
    try {
      const r = await api.get(`/permissions/role/${roleKey}`);
      setSelected(r.data.permissions || []);
      setDefaults(r.data.default_permissions || []);
      setIsCustomized(!!r.data.is_customized);
      setUpdatedAt(r.data.updated_at || "");
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load role permissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeRole) loadRole(activeRole);
  }, [activeRole, loadRole]);

  // ---- mutations ----
  const togglePerm = (key) => {
    setSelected((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      setDirty(true);
      return next;
    });
  };

  const toggleModuleAll = (module) => {
    const moduleKeys = (keysByModule[module] || []).map((p) => p.key);
    const allOn = moduleKeys.every((k) => selected.includes(k));
    setSelected((prev) => {
      let next;
      if (allOn) {
        next = prev.filter((k) => !moduleKeys.includes(k));
      } else {
        next = Array.from(new Set([...prev, ...moduleKeys]));
      }
      setDirty(true);
      return next;
    });
  };

  const collapseAll = () => {
    const next = {};
    Object.keys(keysByModule).forEach((m) => { next[m] = true; });
    setCollapsed(next);
  };
  const expandAll = () => setCollapsed({});

  const save = async () => {
    if (user?.role !== "admin") {
      toast.error("Only admin can save the permission matrix");
      return;
    }
    setSaving(true);
    try {
      await api.put(
        `/permissions/role/${activeRole}`,
        { permissions: selected },
      );
      toast.success(`Saved permissions for ${activeRole}`);
      await loadRole(activeRole);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (user?.role !== "admin") {
      toast.error("Only admin can reset");
      return;
    }
    if (!window.confirm(`Reset ${activeRole} permissions to factory default?`)) return;
    try {
      await api.post(
        `/permissions/role/${activeRole}/reset`,
      );
      toast.success("Reset to default");
      await loadRole(activeRole);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  // ---- derived ----
  const filteredKeysByModule = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return keysByModule;
    const out = {};
    Object.entries(keysByModule).forEach(([mod, items]) => {
      const matches = items.filter(
        (p) => p.key.toLowerCase().includes(q) || (p.label || "").toLowerCase().includes(q),
      );
      if (matches.length > 0) out[mod] = matches;
    });
    return out;
  }, [keysByModule, search]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const totalSelected = selected.length;

  return (
    <div data-testid="perm-matrix-root" className="space-y-6">
      {/* Header bar */}
      <div className="flex flex-wrap items-end justify-between gap-4 bg-white border border-slate-200 p-5 shadow-sm">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Phase B Matrix</div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Permission Matrix
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            <span className="font-semibold text-[#002FA7]">{totalSelected}</span> of{" "}
            <span className="font-semibold">{allKeys.length}</span> permissions enabled for{" "}
            <span className="font-semibold uppercase">{activeRole}</span>
            {isCustomized ? (
              <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">Customized</span>
            ) : (
              <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-300">Factory default</span>
            )}
            {updatedAt && <span className="ml-2 text-xs text-slate-400">· last edit {updatedAt.slice(0, 16).replace("T", " ")}</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="perm-matrix-reset"
            onClick={resetToDefault}
            disabled={user?.role !== "admin" || saving}
            className="rounded-none bg-white border border-slate-300 hover:bg-slate-50 px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowCounterClockwise size={14} weight="bold" /> Reset to default
          </button>
          <button
            data-testid="perm-matrix-save"
            onClick={save}
            disabled={!dirty || user?.role !== "admin" || saving}
            className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Role chip selector */}
      <div data-testid="perm-matrix-roles" className="flex flex-wrap gap-2">
        {LEGACY_ROLES.map((r) => {
          const active = r.key === activeRole;
          return (
            <button
              key={r.key}
              data-testid={`role-chip-${r.key}`}
              onClick={() => {
                if (dirty && !window.confirm("Discard unsaved changes?")) return;
                setActiveRole(r.key);
              }}
              className={`rounded-none px-4 py-2 text-sm font-semibold uppercase tracking-wider border transition-colors ${
                active
                  ? "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white text-slate-700 border-slate-300 hover:border-[#002FA7] hover:text-[#002FA7]"
              }`}
            >
              <ShieldCheck size={12} weight="bold" className="inline mr-1.5 -mt-0.5" />
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Search + collapse controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-slate-50 border border-slate-200 p-3">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="perm-matrix-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search permission key or label…"
            className="rounded-none w-full bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] pl-9 pr-3 py-2 text-sm outline-none"
          />
        </div>
        <div className="flex gap-2">
          <button data-testid="perm-matrix-collapse-all" onClick={collapseAll} className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-white">Collapse all</button>
          <button data-testid="perm-matrix-expand-all" onClick={expandAll} className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-white">Expand all</button>
        </div>
      </div>

      {/* Module groups */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
      ) : (
        <div className="space-y-3">
          {Object.keys(filteredKeysByModule).length === 0 && (
            <div className="text-center text-sm text-slate-500 py-8 bg-white border border-slate-200">No permissions match "{search}"</div>
          )}
          {Object.entries(filteredKeysByModule).map(([module, items]) => {
            const moduleKeys = items.map((p) => p.key);
            const moduleSel = moduleKeys.filter((k) => selectedSet.has(k));
            const allOn = moduleKeys.length > 0 && moduleSel.length === moduleKeys.length;
            const someOn = moduleSel.length > 0 && moduleSel.length < moduleKeys.length;
            const isCollapsed = !!collapsed[module];

            return (
              <div key={module} data-testid={`perm-mod-${module}`} className="bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <button
                    data-testid={`perm-mod-toggle-${module}`}
                    onClick={() => setCollapsed((prev) => ({ ...prev, [module]: !prev[module] }))}
                    className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-700"
                  >
                    {isCollapsed ? <CaretRight size={14} /> : <CaretDown size={14} />}
                    {module}
                    <span className="text-[10px] text-slate-500 ml-1 font-normal normal-case">
                      ({moduleSel.length}/{moduleKeys.length})
                    </span>
                  </button>
                  <button
                    data-testid={`perm-mod-selectall-${module}`}
                    onClick={() => toggleModuleAll(module)}
                    className={`text-xs uppercase tracking-wider font-bold px-3 py-1 border transition-colors ${
                      allOn
                        ? "bg-[#002FA7] text-white border-[#002FA7]"
                        : someOn
                        ? "bg-amber-50 text-amber-800 border-amber-300"
                        : "bg-white text-slate-700 border-slate-300 hover:border-[#002FA7]"
                    }`}
                  >
                    {allOn ? "Deselect all" : someOn ? "Select all" : "Select all"}
                  </button>
                </div>

                {!isCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 p-3">
                    {items.map((p) => {
                      const checked = selectedSet.has(p.key);
                      const isInDefault = defaults.includes(p.key);
                      return (
                        <label
                          key={p.key}
                          data-testid={`perm-key-${p.key}`}
                          className={`flex items-start gap-2 p-2 cursor-pointer transition-colors ${
                            checked ? "bg-blue-50/60" : "hover:bg-slate-50"
                          }`}
                        >
                          <div
                            className={`mt-0.5 w-4 h-4 border flex items-center justify-center shrink-0 ${
                              checked ? "bg-[#002FA7] border-[#002FA7]" : "border-slate-300 bg-white"
                            }`}
                          >
                            {checked && <Check size={10} weight="bold" className="text-white" />}
                          </div>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={checked}
                            onChange={() => togglePerm(p.key)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-800 flex items-center gap-2">
                              {p.label}
                              {!isInDefault && checked && (
                                <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-300 px-1">added</span>
                              )}
                              {isInDefault && !checked && (
                                <span className="text-[9px] uppercase tracking-wider bg-rose-100 text-rose-700 border border-rose-300 px-1">removed</span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono break-all">{p.key}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky bottom save bar */}
      {dirty && (
        <div data-testid="perm-matrix-dirty-bar" className="sticky bottom-0 bg-amber-50 border-2 border-amber-300 px-5 py-3 flex items-center justify-between shadow-lg">
          <div className="text-sm text-amber-900">
            <span className="font-bold">Unsaved changes</span> · {totalSelected} permissions selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { loadRole(activeRole); }}
              className="rounded-none px-4 py-2 text-sm font-medium border border-slate-400 bg-white hover:bg-slate-50"
            >
              Discard
            </button>
            <button
              data-testid="perm-matrix-save-bottom"
              onClick={save}
              disabled={user?.role !== "admin" || saving}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionMatrixTab;
